<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VButtonInvisible } from '@/modules/shared/components'
import { RouterLinkBack } from '@/modules/shared/components'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import MessageDetail from '../components/MessageDetail.vue'
import { useMessagingStore } from '../stores/messaging-store'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const messagingStore = useMessagingStore()

const isAdmin = computed(() => authStore.is_site_or_group_admin)
const isSender = computed(() => messagingStore.currentMessage?.user_id === authStore.current_user?.id)

const skeleton = ref(true)
const pollExpired = ref(false)
let pollInterval: ReturnType<typeof setInterval> | null = null
let pollCount = 0
const MAX_POLL_COUNT = 30 // 30 * 10s = 5 minutes

const hasQueuedLogs = () => {
  return messagingStore.currentMessage?.message_logs.some((l) => l.status === 'queued') ?? false
}

const startPolling = () => {
  if (pollInterval) return
  pollCount = 0
  pollExpired.value = false
  pollInterval = setInterval(async () => {
    pollCount++
    if (!hasQueuedLogs()) {
      stopPolling()
      return
    }
    if (pollCount >= MAX_POLL_COUNT) {
      stopPolling()
      pollExpired.value = true
      return
    }
    try {
      await messagingStore.refreshMessageLogs(route.params.id as string)
      if (!hasQueuedLogs()) stopPolling()
    } catch {
      stopPolling()
    }
  }, 10000)
}

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

const onRefresh = async () => {
  pollExpired.value = false
  try {
    await messagingStore.fetchMessage(route.params.id as string)
    if (hasQueuedLogs()) startPolling()
  } catch {
    notificationStore.enqueue('error', 'Failed to refresh message')
  }
}

onMounted(async () => {
  try {
    await messagingStore.fetchMessage(route.params.id as string)
    if (isAdmin.value && hasQueuedLogs()) startPolling()
  } catch {
    notificationStore.enqueue('error', 'Failed to load message')
  }
  skeleton.value = false
})

onUnmounted(() => {
  stopPolling()
})

const onDelete = async (id: number) => {
  try {
    await messagingStore.destroyMessage(id)
    notificationStore.enqueue('success', 'Message deleted')
    router.push({ name: 'messaging.index' })
  } catch {
    notificationStore.enqueue('error', 'Failed to delete message')
  }
}
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack :to="{ name: 'messaging.index' }">
        <VButtonInvisible>&larr; Back to Messages</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <div v-if="pollExpired" class="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
      Delivery status updates have stopped. <button class="font-medium underline" @click="onRefresh">Refresh</button> to
      check for updates.
    </div>

    <MessageDetail
      :message="messagingStore.currentMessage"
      :skeleton="skeleton"
      :is-admin="isAdmin"
      :is-sender="isSender"
      @delete="onDelete"
    />
  </TheLayout>
</template>
